import dayjs from 'dayjs';
import 'dayjs/locale/pt-br';

const BRL = new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
});

export const formatBRL = (value) => {
    const num = Number(value);
    if (value === null || value === undefined || Number.isNaN(num)) return 'R$ 0,00';
    return BRL.format(num);
};

export const formatDate = (input, fallback = 'N/A') => {
    if (!input) return fallback;
    const d = dayjs(input);
    return d.isValid() ? d.locale('pt-br').format('DD/MM/YYYY') : fallback;
};

export const formatShortDate = (input, fallback = 'N/A') => {
    if (!input) return fallback;
    const d = dayjs(input);
    return d.isValid() ? d.locale('pt-br').format('DD/MM/YY') : fallback;
};

export const formatDateTime = (input, fallback = 'N/A') => {
    if (!input) return fallback;
    const d = dayjs(input);
    return d.isValid() ? d.locale('pt-br').format('DD/MM/YYYY HH:mm') : fallback;
};

export const formatRelativeDate = (input) => {
    if (!input) return null;
    const d = dayjs(input);
    if (!d.isValid()) return null;
    const diff = d.diff(dayjs(), 'day');
    if (diff === 0) return 'Hoje';
    if (diff === 1) return 'Amanhã';
    if (diff === -1) return 'Ontem';
    if (diff > 1 && diff <= 7) return `Em ${diff} dias`;
    if (diff < -1 && diff >= -7) return `Há ${Math.abs(diff)} dias`;
    return d.locale('pt-br').format('DD/MM/YY');
};

export const STATUS_DEVOLUCAO_META = {
    opened:          { label: 'Aberta',          color: 'blue',   description: 'Devolução aberta, ainda sem etiqueta de retorno.' },
    label_generated: { label: 'Etiqueta gerada', color: 'cyan',   description: 'O ML gerou a etiqueta. O comprador ainda não postou o produto.' },
    shipped:         { label: 'Enviada',         color: 'cyan',   description: 'O comprador postou. O produto está a caminho de volta.' },
    delivered:       { label: 'Entregue',        color: 'purple', description: 'O produto de volta chegou ao destino.' },
    closed:          { label: 'Encerrada',       color: 'green',  description: 'Retorno encerrado pelo Mercado Livre.' },
    not_delivered:   { label: 'Não entregue',    color: 'red',    description: 'A entrega ao comprador falhou e o produto voltou para nós.' },
    cancelled:       { label: 'Cancelada',       color: 'gray',   description: 'A devolução foi cancelada. O produto não virá.' },
    failed:          { label: 'Falhada',         color: 'red',    description: 'O retorno falhou. O produto não virá.' },
    expired:         { label: 'Expirada',        color: 'orange', description: 'O prazo acabou e o comprador não postou. O produto não virá.' },
};

export const STATUS_RECLAMACAO_META = {
    opened: {
        label: 'Aberta',
        color: 'yellow',
        description: 'A reclamação segue aberta no Mercado Livre.',
    },
    closed: {
        label: 'Encerrada',
        color: 'green',
        description: 'O Mercado Livre encerrou a reclamação. O produto ainda pode não ter chegado.',
    },
};

export const getStatusReclamacaoLabel = (status) =>
    STATUS_RECLAMACAO_META[status]?.label || status || 'N/A';

export const getStatusReclamacaoColor = (status) =>
    STATUS_RECLAMACAO_META[status]?.color || 'gray';

export const STATUS_MONEY_META = {
    refunded:  { label: 'Reembolsado ao cliente', color: 'red' },
    retained:  { label: 'Retido pelo ML',         color: 'yellow' },
    available: { label: 'Disponível',             color: 'green' },
};

export const TIPO_DEVOLUCAO_META = {
    returns:       { label: 'Devolução' },
    not_delivered: { label: 'Não entregue' },
    change:        { label: 'Troca' },
};

export const REVIEW_RESULT_META = {
    pending: { label: 'Pendente',             color: 'yellow' },
    ok:      { label: 'Chegou como esperado', color: 'green' },
    failed:  { label: 'Chegou com problema',  color: 'red' },
};

export const getStatusDevolucaoLabel = (status) =>
    STATUS_DEVOLUCAO_META[status]?.label || status || 'N/A';

export const getStatusDevolucaoColor = (status) =>
    STATUS_DEVOLUCAO_META[status]?.color || 'gray';

export const getStatusMoneyLabel = (status) =>
    STATUS_MONEY_META[status]?.label || status || 'N/A';

export const getStatusMoneyColor = (status) =>
    STATUS_MONEY_META[status]?.color || 'gray';

export const getTipoDevolucaoLabel = (tipo) =>
    TIPO_DEVOLUCAO_META[tipo]?.label || tipo || 'N/A';

export const URGENCY_META = {
    atrasada: {
        label: 'Atrasada',
        shortLabel: 'ATRASADA',
        color: 'red',
        description: 'Prazo de revisão já venceu e a devolução ainda não foi revisada.',
    },
    urgente: {
        label: 'Revisar hoje',
        shortLabel: 'URGENTE HOJE',
        color: 'red',
        description: 'Último dia útil para revisar no Mercado Livre.',
    },
    proxima: {
        label: 'Próximos dias',
        shortLabel: 'PRÓXIMA',
        color: 'orange',
        description: 'Prazo de revisão se aproxima (até 3 dias úteis).',
    },
    normal: {
        label: 'No prazo',
        shortLabel: 'NO PRAZO',
        color: 'green',
        description: 'Devolução com prazo confortável para revisar.',
    },
    sem_prazo: {
        label: 'Sem prazo',
        shortLabel: 'SEM PRAZO',
        color: 'gray',
        description: 'Devolução já encerrada ou sem prazo informado pelo Mercado Livre.',
    },
};

export const getUrgencyLabel = (level) =>
    URGENCY_META[level]?.label || URGENCY_META.sem_prazo.label;

export const getUrgencyColor = (level) =>
    URGENCY_META[level]?.color || 'gray';

export const formatDeadlineShort = (input, fallback = 'sem prazo') => {
    if (!input) return fallback;
    const d = dayjs(input);
    if (!d.isValid()) return fallback;
    const hoje = dayjs().startOf('day');
    const diff = d.startOf('day').diff(hoje, 'day');
    if (diff === 0) return 'hoje';
    if (diff === 1) return 'amanhã';
    if (diff === -1) return 'ontem';
    if (diff > 1 && diff <= 6) return d.locale('pt-br').format('ddd DD/MM');
    if (diff < -1 && diff >= -6) return `há ${Math.abs(diff)}d`;
    return d.locale('pt-br').format('DD/MM/YY');
};

export const MONEY_OWNER_META = {
    buyer: {
        label: 'Devolvido ao cliente',
        shortLabel: 'DEVOLVIDO AO CLIENTE',
        color: 'red',
        description: 'O reembolso foi enviado ao comprador.',
    },
    ml: {
        label: 'Retido pelo ML',
        shortLabel: 'RETIDO PELO ML',
        color: 'yellow',
        description: 'O Mercado Livre está retendo o valor até a resolução da devolução.',
    },
    seller: {
        label: 'Valor recuperado',
        shortLabel: 'VALOR RECUPERADO',
        color: 'green',
        description: 'A Taga manteve o valor da venda — nenhum reembolso foi enviado.',
    },
    unknown: {
        label: 'Indefinido',
        shortLabel: 'INDEFINIDO',
        color: 'gray',
        description: 'Status financeiro ainda não retornado pela API do Mercado Livre.',
    },
};
